import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { callerBookmarks } from '@/api/callerBookmarks';
import type { CallerBookmarkRow } from '@/api/callerBookmarks';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useEffectiveCustomerId } from '@/lib/customer-context-store';
import { PageHeader } from '@/components/PageHeader';
import { DataTable } from '@/components/DataTable';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MoreHorizontal, Trash2, Search, Phone, Music } from 'lucide-react';

// ─── helpers ─────────────────────────────────────────────────────────────────

/** Format milliseconds as M:SS or H:MM:SS */
function fmtMs(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** Format an ISO date string as a short locale date + time */
function fmtDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

/**
 * Amber progress bar showing how far into the recording the bookmark was saved.
 * If duration is unknown, renders a plain text label instead.
 */
function PositionBar({ positionMs, durationMs }: { positionMs: number; durationMs: number | null }) {
  const posStr = fmtMs(positionMs);
  if (!durationMs || durationMs <= 0) {
    return (
      <span className="font-mono text-xs text-slate-500 tabular-nums">{posStr}</span>
    );
  }
  const pct = Math.min(100, Math.round((positionMs / durationMs) * 100));
  const durStr = fmtMs(durationMs);
  return (
    <div className="flex items-center gap-2 min-w-[140px]">
      {/* track */}
      <div className="relative flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-amber-400"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="font-mono text-[11px] text-slate-500 tabular-nums whitespace-nowrap">
        {posStr}
        <span className="text-slate-300"> / {durStr}</span>
      </span>
    </div>
  );
}

// ─── component ───────────────────────────────────────────────────────────────

const CAN_DELETE = ['super_admin', 'reseller_owner', 'office_manager'];

export function CallerBookmarksList() {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const effectiveCustomerId = useEffectiveCustomerId();
  const qc = useQueryClient();
  const canDelete = CAN_DELETE.includes(user.role);

  const [search, setSearch] = useState('');
  const [draftSearch, setDraftSearch] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<CallerBookmarkRow | null>(null);

  const { data } = useQuery({
    queryKey: ['callerBookmarks', search, effectiveCustomerId],
    queryFn: () => callerBookmarks.list({
      search: search || undefined,
      limit: 200,
      customer_id: effectiveCustomerId ?? undefined,
    }),
  });
  const rows = data?.rows ?? [];

  const remove = useMutation({
    mutationFn: (id: string) => callerBookmarks.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['callerBookmarks'] });
      setDeleteTarget(null);
    },
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(draftSearch.trim());
  };

  const columns = [
    {
      header: 'Caller',
      cell: (r: CallerBookmarkRow) => (
        <span className="flex items-center gap-1.5">
          <Phone className="h-3 w-3 text-slate-400 shrink-0" />
          <span className="font-mono text-xs text-slate-700">{r.from_number}</span>
        </span>
      ),
    },
    {
      header: 'Recording',
      cell: (r: CallerBookmarkRow) => (
        r.recording_name
          ? (
            <span className="flex items-center gap-1.5">
              <Music className="h-3 w-3 text-slate-400 shrink-0" />
              <span className="text-sm text-slate-700 truncate max-w-[220px]" title={r.recording_name}>
                {r.recording_name}
              </span>
            </span>
          )
          : <span className="text-xs text-slate-400 font-mono">{r.recording_id.slice(0, 8)}…</span>
      ),
    },
    {
      header: 'Position',
      cell: (r: CallerBookmarkRow) => (
        <PositionBar positionMs={r.position_ms} durationMs={r.recording_duration_ms} />
      ),
    },
    {
      header: 'Saved',
      cell: (r: CallerBookmarkRow) => (
        <span className="text-xs text-slate-500 whitespace-nowrap">{fmtDate(r.updated_at)}</span>
      ),
    },
    ...(canDelete ? [{
      header: '',
      className: 'w-10 text-end',
      cell: (r: CallerBookmarkRow) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={t('common.actions')}
              onClick={(e) => e.stopPropagation()}
              className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
            <DropdownMenuItem
              className="text-red-600 focus:text-red-600"
              onClick={() => setDeleteTarget(r)}
            >
              <Trash2 className="h-3.5 w-3.5 mr-2" />
              Delete bookmark
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    }] : []),
  ];

  return (
    <div className="space-y-4">
      <PageHeader
        title="Caller Bookmarks"
        description="Saved playback positions — where each caller left off in a recording."
      />

      {/* Search bar */}
      <form onSubmit={handleSearch} className="flex gap-2 max-w-sm">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
          <Input
            placeholder="Search phone or recording…"
            value={draftSearch}
            onChange={(e) => setDraftSearch(e.target.value)}
            className="pl-8 h-8 text-sm"
          />
        </div>
        <Button type="submit" size="sm" variant="secondary">Search</Button>
        {search && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => { setSearch(''); setDraftSearch(''); }}
          >
            Clear
          </Button>
        )}
      </form>

      {/* Table */}
      <DataTable
        columns={columns}
        rows={rows}
        empty={
          search
            ? 'No bookmarks match your search.'
            : 'No caller bookmarks yet. Callers save their place by pressing the bookmark key during playback.'
        }
      />

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete bookmark?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            This will permanently delete the bookmark for{' '}
            <span className="font-mono font-medium">{deleteTarget?.from_number}</span>
            {deleteTarget?.recording_name && (
              <> in <span className="font-medium">{deleteTarget.recording_name}</span></>
            )}.
            The next time the caller calls in they will start from the beginning.
          </p>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">Cancel</Button>
            </DialogClose>
            <Button
              variant="destructive"
              size="sm"
              disabled={remove.isPending}
              onClick={() => deleteTarget && remove.mutate(deleteTarget.id)}
            >
              {remove.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
